import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import type { SavePersonalDetailsResult } from '@/routes/researchers/onboarding/-utils/save-personal-details';

import { SOURCE_OPTIONS } from '@/api/enums';
import { AlertFloating } from '@/components/application/alerts/alerts';
import { Button } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { notify } from '@/components/base/notifications/notifications';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { savePersonalDetails } from '@/routes/researchers/onboarding/-utils/save-personal-details';

/**
 * Map a {@link SavePersonalDetailsResult} failure to a user-facing message.
 * `forbidden-role` / `unauthenticated` are race conditions; the
 * onboarding guards in `onboarding.tsx` should keep them out of this form.
 */
function failureMessage(
  result: SavePersonalDetailsResult & { success: false },
  fallback: string,
): string {
  if (result.message) return result.message;
  switch (result.reason) {
    case 'unauthenticated':
      return 'Your session has expired. Please sign in again.';
    case 'forbidden-role':
      return 'Only researcher accounts can complete this step.';
    case 'bad-request':
      return 'Please double-check the details and try again.';
    default:
      // `internal` falls through here; we don't have a more specific
      // message because the underlying core-api error wasn't recognised.
      return fallback;
  }
}

export const Route = createFileRoute('/researchers/onboarding/personal')({
  beforeLoad: ({ context }) => {
    if (context.orgs.length === 0) {
      throw redirect({ to: '/researchers/onboarding/organization' });
    }
  },
  component: OnboardingPersonalPage,
});

const sourceItems = SOURCE_OPTIONS.map((value) => ({
  id: value,
  label: value,
}));

function OnboardingPersonalPage() {
  const { t } = useLingui();

  const { user } = Route.useRouteContext();
  const navigate = useNavigate();

  const schema = z.object({
    jobTitle: z
      .string()
      .trim()
      .min(3, t`Job title too short`),
    source: z.enum(SOURCE_OPTIONS, {
      message: t`Please choose how you heard about us`,
    }),
  });
  type FormFields = z.infer<typeof schema>;

  const form = useForm({
    defaultValues: { jobTitle: user.jobTitle, source: user.source ?? '' },
    validators: { onSubmit: schema },
    onSubmit: ({ value }) =>
      personalDetailsMutation.mutate(value as FormFields, {
        onSuccess: () => {
          notify({ title: t`Details saved`, color: 'success' });
          navigate({ to: '/researchers/projects', reloadDocument: true });
        },
      }),
  });

  const personalDetailsMutation = useMutation({
    mutationFn: async ({ jobTitle, source }: FormFields) => {
      // Direct server-function call — no BFF HTTP round-trip.
      const result = await savePersonalDetails({
        data: { jobTitle: jobTitle.trim(), source },
      });
      if (!result.success) {
        throw new Error(failureMessage(result, t`Failed to save your details`));
      }
    },
  });

  return (
    <AuthLayout rightPanel={<OnboardingPanel />}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-8"
      >
        <div>
          <p className="text-sm font-semibold tracking-[0.16em] text-brand-secondary uppercase">
            <Trans>Step 2 of 2</Trans>
          </p>
          <h1 className="mt-3 font-heading text-display-xs font-semibold text-primary">
            <Trans>Tell us a little about yourself</Trans>
          </h1>
          <p className="mt-2 text-md text-secondary">
            <Trans>
              We use this to tailor your experience and to help match you with
              the right resources.
            </Trans>
          </p>
        </div>

        <div className="flex flex-col gap-5">
          <FormInput
            type="text"
            form={form}
            field="jobTitle"
            label={t`What's your job title?`}
            placeholder={t`Product Designer`}
            isRequired
            size="md"
          />
          <FormInput
            type="select"
            form={form}
            field="source"
            label={t`How did you hear about us?`}
            placeholder={t`Select an option`}
            items={sourceItems}
            size="md"
          />
        </div>

        {personalDetailsMutation.error && (
          <AlertFloating
            color="error"
            title={t`Something went wrong`}
            description={personalDetailsMutation.error.message}
            confirmLabel=""
          />
        )}

        <form.Subscribe
          selector={(state) => state.isSubmitting}
          children={(isSubmitting) => (
            <Button
              type="submit"
              color="primary"
              size="lg"
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
              className="w-full"
              data-testid="researcher-onboarding-finish-button"
            >
              <Trans>Finish setup</Trans>
            </Button>
          )}
        />
      </form>
    </AuthLayout>
  );
}

function OnboardingPanel() {
  return (
    <div className="relative flex h-full min-h-180 flex-col justify-between overflow-hidden bg-brand-section px-10 py-12 text-white">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.22),transparent_34%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.14),transparent_30%)]" />
      <div className="relative">
        <p className="text-sm font-semibold tracking-[0.16em] text-white/70 uppercase">
          <Trans>Last step</Trans>
        </p>
        <h2 className="mt-4 max-w-md font-heading text-display-md font-semibold text-white">
          <Trans>Just a couple of details and you&apos;re done.</Trans>
        </h2>
        <p className="mt-4 max-w-md text-lg text-white/80">
          <Trans>
            We&apos;ll use these to surface the most relevant onboarding tips
            and templates for your role.
          </Trans>
        </p>
      </div>
    </div>
  );
}
